/**
 * Ribbon designer for organization admins (docs/Decisions.md D42): create
 * and edit the org's ribbons and badges - stripes, device, achievement
 * levels, award criteria and visibility scope - with a live preview, archive
 * and restore them, and set the org's rack layout.
 *
 * Premium `ribbon-designer`; group and organization scopes need
 * `badge-visibility`. A basic org sees the upgrade prompt instead.
 */
import { useEffect, useMemo, useState } from 'react';
import { ui, cn } from '../../lib/ui';
import {
  STRIPE_TOKENS,
  MEDAL_TOKENS,
  TOKEN_LABELS,
  DEVICE_TYPES,
  DEVICE_LABELS,
  METRIC_LABELS,
  LIMITS,
  isToken,
  emptyDraft,
  draftFromDefinition,
  validateDraft,
  definitionBody,
  previewItem,
  moveItem,
  scopeLabel,
} from '../../lib/ribbons';
import * as ribbonsApi from '../../api/ribbons';
import { listGroups } from '../../api/groups';
import { ErrorAlert } from '../ui';
import { useToast } from '../toast';
import RibbonBar from './RibbonBar';
import BadgeMedallion from './BadgeMedallion';
import { TextInput, SelectInput, PremiumNotice, MoveButtons, REVIEW_NOTICE } from './parts';
import { useAuth } from '../../lib/auth';

const CUSTOM = 'custom';

function StripeEditor({ stripes, onChange }) {
  function update(index, patch) {
    onChange(stripes.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }
  return (
    <fieldset className="mb-4">
      <legend className={ui.label}>Stripes, left to right</legend>
      <ol className="flex flex-col gap-2">
        {stripes.map((stripe, index) => {
          const custom = !isToken(stripe.color);
          return (
            <li key={index} className={cn(ui.row, 'items-end')}>
              <SelectInput
                label={`Stripe ${index + 1} colour`}
                className="mb-0!"
                value={custom ? CUSTOM : stripe.color}
                onChange={e =>
                  update(index, { color: e.target.value === CUSTOM ? '' : e.target.value })
                }
              >
                {STRIPE_TOKENS.map(token => (
                  <option key={token} value={token}>
                    {TOKEN_LABELS[token]}
                  </option>
                ))}
                <option value={CUSTOM}>Custom colour</option>
              </SelectInput>
              {custom && (
                <TextInput
                  label="Hex colour"
                  className="mb-0! w-32"
                  value={stripe.color}
                  placeholder="#RRGGBB"
                  onChange={e => update(index, { color: e.target.value.trim().toUpperCase() })}
                />
              )}
              <TextInput
                label="Width"
                type="number"
                min="1"
                max={LIMITS.maxStripeWidth}
                className="mb-0! w-20"
                value={stripe.width}
                onChange={e => update(index, { width: parseInt(e.target.value, 10) || 0 })}
              />
              <MoveButtons
                label={`stripe ${index + 1}`}
                index={index}
                count={stripes.length}
                onMove={(i, d) => onChange(moveItem(stripes, i, d))}
              />
              <button
                type="button"
                className={cn(ui.btnGhost, ui.btnSm)}
                onClick={() => onChange(stripes.filter((_, i) => i !== index))}
                disabled={stripes.length === 1}
                aria-label={`Remove stripe ${index + 1}`}
              >
                Remove
              </button>
            </li>
          );
        })}
      </ol>
      <button
        type="button"
        className={cn(ui.btnSecondary, ui.btnSm, 'mt-2')}
        onClick={() => onChange([...stripes, { color: 'logo-blue', width: 1 }])}
        disabled={stripes.length >= LIMITS.maxStripes}
      >
        Add stripe
      </button>
    </fieldset>
  );
}

function LevelEditor({ levels, metric, onChange }) {
  function update(index, patch) {
    onChange(levels.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }
  return (
    <fieldset className="mb-4">
      <legend className={ui.label}>Achievement levels, lowest first</legend>
      <p className={ui.hint}>
        Leave empty for a single ribbon.{' '}
        {metric
          ? 'Each level needs a higher threshold than the one before.'
          : 'Staff set the level when they award it.'}
      </p>
      <ol className="mt-2 flex flex-col gap-2">
        {levels.map((level, index) => (
          <li key={index} className={cn(ui.row, 'items-end')}>
            <TextInput
              label={`Level ${index + 1} name`}
              className="mb-0!"
              value={level.name}
              maxLength={40}
              onChange={e => update(index, { name: e.target.value })}
            />
            {metric && (
              <TextInput
                label="Threshold"
                type="number"
                min="1"
                className="mb-0! w-28"
                value={level.threshold ?? ''}
                onChange={e =>
                  update(index, { threshold: parseInt(e.target.value, 10) || undefined })
                }
              />
            )}
            <SelectInput
              label="Device colour"
              className="mb-0!"
              value={level.deviceColor || 'medal-gold'}
              onChange={e => update(index, { deviceColor: e.target.value })}
            >
              {MEDAL_TOKENS.map(token => (
                <option key={token} value={token}>
                  {TOKEN_LABELS[token]}
                </option>
              ))}
            </SelectInput>
            <MoveButtons
              label={`level ${index + 1}`}
              index={index}
              count={levels.length}
              onMove={(i, d) => onChange(moveItem(levels, i, d))}
            />
            <button
              type="button"
              className={cn(ui.btnGhost, ui.btnSm)}
              onClick={() => onChange(levels.filter((_, i) => i !== index))}
              aria-label={`Remove level ${index + 1}`}
            >
              Remove
            </button>
          </li>
        ))}
      </ol>
      <button
        type="button"
        className={cn(ui.btnSecondary, ui.btnSm, 'mt-2')}
        onClick={() =>
          onChange([
            ...levels,
            {
              name: '',
              deviceColor: MEDAL_TOKENS[Math.min(levels.length, MEDAL_TOKENS.length - 1)],
            },
          ])
        }
        disabled={levels.length >= LIMITS.maxLevels}
      >
        Add level
      </button>
    </fieldset>
  );
}

function Preview({ draft }) {
  const levels = draft.levels.length ? draft.levels.map((_, i) => i) : [0];
  return (
    <div className={cn(ui.cardFlat, 'p-4!')} aria-live="polite">
      <p className={cn(ui.eyebrow, 'mb-2')}>Preview</p>
      <ul className="flex flex-wrap items-end gap-4">
        {levels.map(index => {
          const item = previewItem(draft, index);
          return (
            <li key={index} className="flex flex-col items-center gap-1">
              {draft.kind === 'badge' ? <BadgeMedallion item={item} /> : <RibbonBar item={item} />}
              <span className="text-caption text-dcon-ink-muted">
                {item.level ? item.level.name : item.name}
              </span>
            </li>
          );
        })}
      </ul>
      <p className={cn(ui.hint, 'mt-2')}>{scopeLabel(draft.visibility)}</p>
    </div>
  );
}

function DesignForm({ initial, features, groups, onSaved, onCancel, isOrgAdmin }) {
  const [draft, setDraft] = useState(initial.draft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();
  const problems = useMemo(() => validateDraft(draft), [draft]);
  const metric = draft.criteria.type === 'metric';

  const set = patch => setDraft(d => ({ ...d, ...patch }));

  async function save(e) {
    e.preventDefault();
    if (problems.length) return;
    setBusy(true);
    setError(null);
    try {
      const body = definitionBody(draft);
      // docs/Decisions.md D60: a new design for a revoked ribbon changes only
      // its look and words, and waits for the platform's review.
      const result = initial.resubmit
        ? await ribbonsApi.updateDefinition(initial.ribbonId, {
            resubmit: {
              name: body.name,
              description: body.description,
              design: body.design,
              levels: body.levels,
            },
          })
        : initial.ribbonId
          ? await ribbonsApi.updateDefinition(initial.ribbonId, body)
          : await ribbonsApi.createDefinition(body);
      toast.success(
        initial.resubmit
          ? 'New design sent for review'
          : initial.ribbonId
            ? 'Ribbon saved'
            : 'Ribbon created'
      );
      onSaved(result.definition);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={save}
      className={ui.card}
      aria-label={
        initial.resubmit ? 'New design for review' : initial.ribbonId ? 'Edit ribbon' : 'New ribbon'
      }
    >
      <h3 className={ui.h3}>
        {initial.resubmit
          ? 'Submit a new design'
          : initial.ribbonId
            ? `Edit ${initial.draft.name}`
            : 'New ribbon or badge'}
      </h3>
      {initial.resubmit ? (
        <p className={cn(ui.hint, 'mb-4')}>
          The platform revoked the previous design. Members who hold this ribbon see a plain
          placeholder ribbon until the platform approves a new design; how it is earned does not
          change.
        </p>
      ) : null}
      <ErrorAlert error={error} />
      <div className={cn(ui.grid, ui.gridMd2)}>
        <div>
          <SelectInput
            label="Kind"
            value={draft.kind}
            onChange={e => set({ kind: e.target.value })}
          >
            <option value="ribbon">Ribbon</option>
            <option value="badge">Badge</option>
          </SelectInput>
          <TextInput
            label="Name"
            value={draft.name}
            maxLength={LIMITS.maxName}
            required
            onChange={e => set({ name: e.target.value })}
          />
          <TextInput
            label="Description"
            value={draft.description}
            maxLength={280}
            hint="Shown in the tooltip, for example what earns it."
            onChange={e => set({ description: e.target.value })}
          />
        </div>
        <Preview draft={draft} />
      </div>

      <StripeEditor
        stripes={draft.design.stripes}
        onChange={stripes => set({ design: { ...draft.design, stripes } })}
      />

      <SelectInput
        label="Center device"
        hint="Drawn in the level's medal colour."
        value={draft.design.device}
        onChange={e => set({ design: { ...draft.design, device: e.target.value } })}
      >
        {DEVICE_TYPES.map(d => (
          <option key={d} value={d}>
            {DEVICE_LABELS[d]}
          </option>
        ))}
      </SelectInput>

      <fieldset className="mb-4">
        <legend className={ui.label}>How it is earned</legend>
        <div className={ui.row}>
          <label className="inline-flex items-center gap-2 text-body-s">
            <input
              type="radio"
              className={ui.checkbox}
              name="criteria"
              checked={!metric}
              onChange={() => set({ criteria: { type: 'manual' } })}
            />
            Awarded by staff
          </label>
          <label className="inline-flex items-center gap-2 text-body-s">
            <input
              type="radio"
              className={ui.checkbox}
              name="criteria"
              checked={metric}
              onChange={() =>
                set({
                  criteria: { type: 'metric', metric: draft.criteria.metric || 'forum_posts' },
                })
              }
            />
            Earned automatically from activity in this organization
          </label>
        </div>
        {metric && (
          <div className={cn(ui.row, 'mt-2 items-end')}>
            <SelectInput
              label="Activity"
              className="mb-0!"
              value={draft.criteria.metric}
              onChange={e => set({ criteria: { ...draft.criteria, metric: e.target.value } })}
            >
              {Object.entries(METRIC_LABELS).map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </SelectInput>
            {draft.levels.length === 0 && (
              <TextInput
                label="Times needed"
                type="number"
                min="1"
                className="mb-0! w-28"
                value={draft.criteria.threshold ?? ''}
                onChange={e =>
                  set({
                    criteria: {
                      ...draft.criteria,
                      threshold: parseInt(e.target.value, 10) || undefined,
                    },
                  })
                }
              />
            )}
          </div>
        )}
      </fieldset>

      <LevelEditor levels={draft.levels} metric={metric} onChange={levels => set({ levels })} />

      <fieldset className="mb-4">
        <legend className={ui.label}>
          Who can see it{' '}
          {!features.badgeVisibility && <span className={ui.pillPremium}>Premium scopes</span>}
        </legend>
        <div className={cn(ui.row, 'items-end')}>
          <SelectInput
            label="Visibility"
            className="mb-0!"
            value={draft.visibility.scope}
            onChange={e => set({ visibility: { scope: e.target.value } })}
          >
            <option value="platform">Platform wide</option>
            <option value="organization" disabled={!features.badgeVisibility}>
              Members of this organization
            </option>
            <option value="group" disabled={!features.badgeVisibility}>
              One group
            </option>
          </SelectInput>
          {draft.visibility.scope === 'group' && (
            <SelectInput
              label="Group"
              className="mb-0!"
              value={draft.visibility.groupId || ''}
              onChange={e => set({ visibility: { scope: 'group', groupId: e.target.value } })}
            >
              <option value="">Choose a group</option>
              {groups.map(g => (
                <option key={g.groupId} value={g.groupId}>
                  {g.name}
                </option>
              ))}
            </SelectInput>
          )}
        </div>
        {!features.badgeVisibility && (
          <div className="mt-2">
            <PremiumNotice feature="badge-visibility" isOrgAdmin={isOrgAdmin} />
          </div>
        )}
      </fieldset>

      {problems.length > 0 && (
        <div className={ui.alertWarning} role="status">
          <p className="mb-1 font-semibold">Before you save</p>
          <ul className="list-disc pl-5">
            {problems.map(p => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={ui.row}>
        <button type="submit" className={ui.btnPrimary} disabled={busy || problems.length > 0}>
          {busy ? 'Saving' : initial.ribbonId ? 'Save ribbon' : 'Create ribbon'}
        </button>
        <button type="button" className={ui.btnSecondary} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function LayoutEditor({ definitions, catalog }) {
  const [layout, setLayout] = useState(null);
  const [order, setOrder] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const options = useMemo(() => {
    const platform = (catalog || []).map(r => ({
      key: `ribbon:${r.ribbonId}`,
      item: { ...r, level: null },
      source: 'Platform',
    }));
    const own = definitions
      .filter(d => d.status === 'active' && d.kind === 'ribbon')
      .map(d => ({
        key: `org:${d.orgId}:${d.ribbonId}`,
        item: previewItem(draftFromDefinition(d)),
        source: 'This organization',
      }));
    return [...own, ...platform];
  }, [definitions, catalog]);

  useEffect(() => {
    ribbonsApi
      .getRackLayout()
      .then(data => setLayout(data.layout))
      .catch(setError);
  }, []);

  useEffect(() => {
    if (!layout) return;
    const ranked = layout.precedence.filter(k => options.some(o => o.key === k));
    const rest = options.map(o => o.key).filter(k => !ranked.includes(k));
    setOrder([...ranked, ...rest]);
  }, [layout, options]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const result = await ribbonsApi.putRackLayout({
        precedence: order,
        ribbonsPerRow: layout.ribbonsPerRow,
        showLevels: layout.showLevels,
      });
      setLayout(result.layout);
      toast.success('Rack layout saved');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (!layout) return <ErrorAlert error={error} />;
  const byKey = new Map(options.map(o => [o.key, o]));

  return (
    <section className={ui.card} aria-labelledby="rack-layout-heading">
      <h3 id="rack-layout-heading" className={ui.h3}>
        Rack layout
      </h3>
      <p className={cn(ui.muted, 'mb-4 text-body-s')}>
        The order ribbons take on every member&rsquo;s rack when viewed in this organization.
        Members can still arrange their own.
      </p>
      <ErrorAlert error={error} />
      <div className={cn(ui.row, 'items-end')}>
        <TextInput
          label="Ribbons per row"
          type="number"
          min="1"
          max="12"
          className="w-36"
          value={layout.ribbonsPerRow}
          onChange={e => setLayout({ ...layout, ribbonsPerRow: parseInt(e.target.value, 10) || 1 })}
        />
        <label className={cn(ui.field, 'inline-flex items-center gap-2 text-body-s')}>
          <input
            type="checkbox"
            className={ui.checkbox}
            checked={layout.showLevels}
            onChange={e => setLayout({ ...layout, showLevels: e.target.checked })}
          />
          Show level devices
        </label>
      </div>
      <ol className="mb-4 flex flex-col gap-2" aria-label="Ribbon precedence, highest first">
        {order.map((key, index) => {
          const option = byKey.get(key);
          if (!option) return null;
          return (
            <li key={key} className={cn(ui.row, 'rounded-dcon-md border border-z-line px-3 py-2')}>
              <span className="w-6 text-caption text-dcon-ink-subtle tabular-nums">
                {index + 1}
              </span>
              <RibbonBar item={option.item} showLevel={false} />
              <span className="flex-1 text-body-s text-dcon-ink">{option.item.name}</span>
              <span className={ui.pillNeutral}>{option.source}</span>
              <MoveButtons
                label={option.item.name}
                index={index}
                count={order.length}
                onMove={(i, d) => setOrder(moveItem(order, i, d))}
              />
            </li>
          );
        })}
      </ol>
      <button type="button" className={ui.btnPrimary} onClick={save} disabled={busy}>
        {busy ? 'Saving' : 'Save layout'}
      </button>
    </section>
  );
}

export default function RibbonDesigner({ isOrgAdmin }) {
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);
  const [revoking, setRevoking] = useState(null); // { ribbonId, reason, reject? }
  const toast = useToast();
  const { user } = useAuth();
  const isPlatformAdmin = user?.platformRole === 'platform-admin';

  function load() {
    ribbonsApi.listDefinitions().then(setData).catch(setError);
  }

  // docs/Decisions.md D51 item 4: the platform's review action.
  async function revoke(def, reason) {
    try {
      await ribbonsApi.updateDefinition(def.ribbonId, { status: 'revoked', reason });
      toast.success('Design revoked');
      setRevoking(null);
      load();
    } catch (err) {
      setError(err);
    }
  }

  async function reviewResubmission(def, approve, reason) {
    try {
      await ribbonsApi.updateDefinition(
        def.ribbonId,
        approve ? { approveResubmission: true } : { rejectResubmission: true, reason }
      );
      toast.success(approve ? 'New design approved' : 'New design rejected');
      setRevoking(null);
      load();
    } catch (err) {
      setError(err);
    }
  }

  async function restoreRevoked(def) {
    try {
      await ribbonsApi.updateDefinition(def.ribbonId, { restore: true });
      toast.success('Design restored as archived');
      load();
    } catch (err) {
      setError(err);
    }
  }

  useEffect(() => {
    load();
    ribbonsApi
      .getRibbonCatalog()
      .then(c => setCatalog(c.ribbons || []))
      .catch(() => setCatalog([]));
    listGroups()
      .then(g => setGroups(g.groups || []))
      .catch(() => setGroups([]));
  }, []);

  async function setStatus(def, status) {
    try {
      await ribbonsApi.updateDefinition(def.ribbonId, { status });
      toast.success(status === 'archived' ? 'Ribbon archived' : 'Ribbon restored');
      load();
    } catch (err) {
      setError(err);
    }
  }

  if (error && !data) return <ErrorAlert error={error} />;
  if (!data) {
    return (
      <p className={ui.muted} role="status">
        Loading designs
      </p>
    );
  }
  if (!data.features.ribbonDesigner) return <PremiumNotice isOrgAdmin={isOrgAdmin} />;
  if (!data.canManage) {
    return <p className={ui.muted}>Only organization admins can design ribbons.</p>;
  }

  if (editing) {
    return (
      <DesignForm
        key={editing.ribbonId || 'new'}
        initial={editing}
        features={data.features}
        groups={groups}
        isOrgAdmin={isOrgAdmin}
        onCancel={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <section className={ui.card} aria-labelledby="designs-heading">
        <div className={cn(ui.row, ui.spread, 'mb-4')}>
          <h3 id="designs-heading" className={cn(ui.h3, 'mb-0!')}>
            Your organization&rsquo;s ribbons and badges
          </h3>
          <button
            type="button"
            className={ui.btnPrimary}
            onClick={() => setEditing({ ribbonId: null, draft: emptyDraft() })}
          >
            New ribbon
          </button>
        </div>
        <p className={cn(ui.hint, 'mb-4')}>{REVIEW_NOTICE}</p>
        <ErrorAlert error={error} />
        {data.definitions.length === 0 ? (
          <p className={ui.muted}>
            No designs yet. Create one to recognise what matters in your organization.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {data.definitions.map(def => {
              const item = previewItem(
                draftFromDefinition(def),
                Math.max(0, (def.levels || []).length - 1)
              );
              return (
                <li
                  key={def.ribbonId}
                  className={cn(ui.row, 'rounded-dcon-md border border-z-line px-3 py-2')}
                >
                  {def.kind === 'badge' ? (
                    <BadgeMedallion item={item} />
                  ) : (
                    <RibbonBar item={item} />
                  )}
                  <span className="flex-1">
                    <span className="block font-semibold text-dcon-ink">{def.name}</span>
                    <span className="block text-caption text-dcon-ink-muted">
                      {def.criteria.type === 'metric'
                        ? METRIC_LABELS[def.criteria.metric]
                        : 'Awarded by staff'}
                      {def.levels.length ? `, ${def.levels.length} levels` : ''}.{' '}
                      {scopeLabel(def.visibility)}
                    </span>
                    {def.status === 'revoked' && def.revokeReason ? (
                      <span className="block text-caption text-dcon-danger">
                        Revoked by the platform: {def.revokeReason}
                      </span>
                    ) : null}
                    {def.status === 'revoked' ? (
                      <span className="block text-caption text-dcon-ink-muted">
                        {def.pendingDesign
                          ? `A new design, "${def.pendingDesign.name}", is waiting for the platform's review.`
                          : 'Members see a plain placeholder ribbon in its place.'}
                      </span>
                    ) : null}
                  </span>
                  {def.status === 'archived' && <span className={ui.pillWarning}>Archived</span>}
                  {def.status === 'revoked' ? (
                    <>
                      <span className={ui.pillDanger} title={def.revokeReason || undefined}>
                        Revoked by the platform
                      </span>
                      {def.createdBy === user?.userId && !def.pendingDesign ? (
                        <button
                          type="button"
                          className={cn(ui.btnSecondary, ui.btnSm)}
                          onClick={() =>
                            setEditing({
                              ribbonId: def.ribbonId,
                              resubmit: true,
                              draft: draftFromDefinition(def),
                            })
                          }
                        >
                          Submit a new design
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={cn(ui.btnSecondary, ui.btnSm)}
                        onClick={() =>
                          setEditing({ ribbonId: def.ribbonId, draft: draftFromDefinition(def) })
                        }
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className={cn(ui.btnGhost, ui.btnSm)}
                        onClick={() =>
                          setStatus(def, def.status === 'archived' ? 'active' : 'archived')
                        }
                      >
                        {def.status === 'archived' ? 'Restore' : 'Archive'}
                      </button>
                    </>
                  )}
                  {isPlatformAdmin && def.status !== 'revoked' ? (
                    <button
                      type="button"
                      className={cn(ui.btnDanger, ui.btnSm)}
                      onClick={() => setRevoking({ ribbonId: def.ribbonId, reason: '' })}
                    >
                      Revoke
                    </button>
                  ) : null}
                  {isPlatformAdmin && def.status === 'revoked' ? (
                    <button
                      type="button"
                      className={cn(ui.btnGhost, ui.btnSm)}
                      onClick={() => restoreRevoked(def)}
                    >
                      Undo revoke
                    </button>
                  ) : null}
                  {isPlatformAdmin && def.status === 'revoked' && def.pendingDesign ? (
                    <span className={cn(ui.row, 'w-full')}>
                      <RibbonBar
                        item={previewItem(
                          draftFromDefinition({ ...def, ...def.pendingDesign }),
                          Math.max(0, (def.pendingDesign.levels || []).length - 1)
                        )}
                      />
                      <span className="flex-1 text-caption">
                        New design for review: {def.pendingDesign.name}
                      </span>
                      <button
                        type="button"
                        className={cn(ui.btnPrimary, ui.btnSm)}
                        onClick={() => reviewResubmission(def, true)}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className={cn(ui.btnGhost, ui.btnSm)}
                        onClick={() =>
                          setRevoking({ ribbonId: def.ribbonId, reason: '', reject: true })
                        }
                      >
                        Reject
                      </button>
                    </span>
                  ) : null}
                  {revoking?.ribbonId === def.ribbonId ? (
                    <form
                      className={cn(ui.row, 'w-full')}
                      onSubmit={e => {
                        e.preventDefault();
                        if (!revoking.reason.trim()) return;
                        if (revoking.reject) reviewResubmission(def, false, revoking.reason.trim());
                        else revoke(def, revoking.reason.trim());
                      }}
                    >
                      <TextInput
                        label="Reason (shown to the organization)"
                        value={revoking.reason}
                        maxLength={500}
                        onChange={e => setRevoking({ ...revoking, reason: e.target.value })}
                      />
                      <button
                        type="submit"
                        className={cn(ui.btnDanger, ui.btnSm)}
                        disabled={!revoking.reason.trim()}
                      >
                        {revoking.reject ? 'Reject new design' : 'Revoke design'}
                      </button>
                      <button
                        type="button"
                        className={cn(ui.btnGhost, ui.btnSm)}
                        onClick={() => setRevoking(null)}
                      >
                        Cancel
                      </button>
                    </form>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>
      <LayoutEditor definitions={data.definitions} catalog={catalog} />
    </div>
  );
}
